import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { NoodleBriefQuestionGroupDto, NoodleQuestionDto } from '../../common/general-types/noodle.dto';
import { Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NoodleService {

  private http = inject(HttpClient);
  private firebaseService = inject(FirebaseService);

  constructor() { }

  getBriefQuestions(briefId: string): Observable<NoodleBriefQuestionGroupDto> {
    const get_brief_questions_url = `${environment.shout_api.url}/video-ai-moderate/human-in-loop/questions/${briefId}`;
    return this.http.get<NoodleBriefQuestionGroupDto>(get_brief_questions_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap((group) => {
        console.log('[NoodleService.getBriefQuestions]', get_brief_questions_url, group?.questions);
      }),
    );
  }

  getBriefExists(briefId: string): Observable<NoodleBriefQuestionGroupDto | { success: boolean, message: string }> {
    const get_brief_questions_url = `${environment.shout_api.url}/video-ai-moderate/human-in-loop/questions/${briefId}`;
    return this.http.get<NoodleBriefQuestionGroupDto>(get_brief_questions_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getQuestionEnrichment(briefId: string) {
    const get_brief_enrichments_url = `${environment.noodleApi.baseUrl}/brief_enrichments?generalisedId=${briefId}`;

    return this.http.get(get_brief_enrichments_url, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': `${environment.noodleApi.api_key}`,
      }
    });
  }

  saveQuestionEnrichments(questions: NoodleBriefQuestionGroupDto[]) {
    const post_question_enrichments_url = `${environment.shout_api.url}/video-ai-moderate/human-in-loop/update-questions`;
    let postPacket: {
      briefId: string;
      briefIntId: string | null;
      briefName: string;
      briefStatus: string;
      briefType: string;
      organizationId: string;
      questionsCount: number;
      questions: {
        id: string;
        type: string;
        statement: string;
        questionType: number;
        subjectiveType: number;
        enrichments: { text: string }[];
      }[]
    } = {
      briefId: questions[0].briefId,
      briefIntId: questions[0].briefIntId,
      briefName: questions[0].briefName,
      briefStatus: questions[0].briefStatus,
      briefType: questions[0].briefType,
      organizationId: questions[0].organizationId,
      questionsCount: questions[0].questionsCount,
      questions: questions[0].questions.map((question: NoodleQuestionDto) => ({
        id: question.id,
        type: question.type,
        statement: question.statement,
        questionType: question.questionType,
        subjectiveType: question.subjectiveType,
        enrichments: question.enrichments ?? []
      }))
    }
    return this.http.post<NoodleBriefQuestionGroupDto>(post_question_enrichments_url, postPacket, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getScreeningResultsDirect(contentId: string, briefId: string): Observable<any> {
    const get_screening_results_url = `${environment.noodleApi.baseUrl}/get_results?contentId=${contentId}&briefId=${briefId}`;
    return this.http.get<any>(get_screening_results_url, {
      headers: {
        'Content-Type': 'application/json',
        'apikey': `${environment.noodleApi.api_key}`,
      }
    });
  }

  /**
   * Lowers raised flags. Each override is one review row. `id` is that row's id from
   * `get_results`, and `newAnswer` is always `Approved`. The note is stored as `explanation`.
   */
  updateDecision(contentId: string, reviewResultIds: readonly string[], note: string): Observable<unknown> {
    const decision_update_url = `${environment.noodleApi.baseUrl}/decision_update`;
    return this.http.post<unknown>(decision_update_url, {
      overrides: reviewResultIds.map((id) => ({
        id,
        contentId,
        decision: 'approved',
        newAnswer: 'Approved',
        note,
        explanation: note,
      })),
    }, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': `${environment.noodleApi.api_key}`,
      },
    });
  }

}
